/* component-free-gift-bar.js — <free-gift-bar>
   BXGY free-gift progress bar. Works inside the cart drawer AND on the cart page.

   Server (snippets/free-gift-bar.liquid) computes the display state and stamps
   data attributes:
     data-threshold        gift threshold in cents
     data-cart-total       cart total_price in cents at render time
     data-gift-variant-id  the gift product's first-available variant id
     data-gift-in-cart     'true' if the gift line is present
     data-auto-remove      'true' to auto-remove the gift when cart drops below

   Behaviour:
     - "Gratis-Geschenk hinzufügen" button → KK.cart.add of the gift variant with
       a { _gift: 'true' } line property, so the cart page + drawer can identify
       the gift line and the BXGY automatic discount makes it 0 €.
     - Reacts to kk:cart:updated. In the drawer, the section re-renders itself from
       the Section Rendering API, so this component just re-binds on reconnect. On
       the cart page (no auto re-render), it re-evaluates threshold crossing:
         · if auto-remove is ON and the cart dropped below threshold while the gift
           is present → remove the gift line (KK.cart.change to 0).
         · if the gift is now present (e.g. just added) while the add button is
           still shown → neutralise the stale add button so a second click can't
           add a SECOND gift line (the cart page has no auto re-render to swap it).
         · otherwise it leaves the server-rendered hint in place.
     - The gift is added ONCE (guards against double-add / re-entrancy).

   Talks to the cart ONLY through window.KK.cart. ES module, one component/file. */

class FreeGiftBar extends HTMLElement {
  connectedCallback() {
    this.threshold = parseInt(this.getAttribute('data-threshold') || '0', 10);
    this.giftVariantId = this.getAttribute('data-gift-variant-id') || '';
    this.autoRemove = this.getAttribute('data-auto-remove') === 'true';
    this._adding = false;
    this._removing = false;

    const btn = this.querySelector('[data-gift-add]');
    if (btn) {
      this._onAdd = (e) => { e.preventDefault(); this.addGift(); };
      btn.addEventListener('click', this._onAdd);
      btn.addEventListener('touchend', (e) => { e.preventDefault(); this.addGift(); }, { passive: false });
      const form = btn.closest('form[data-gift-form]');
      if (form) form.addEventListener('submit', (e) => { e.preventDefault(); this.addGift(); });
      this._addBtn = btn;
    }

    if (window.KK && window.KK.pubsub) {
      this._unsub = window.KK.pubsub.subscribe('kk:cart:updated', (p) => this._onCartUpdated(p));
    }
  }

  disconnectedCallback() {
    if (this._addBtn && this._onAdd) this._addBtn.removeEventListener('click', this._onAdd);
    if (this._unsub) this._unsub();
  }

  addGift() {
    if (this._adding || !this.giftVariantId) return;
    this._adding = true;
    if (this._addBtn) this._addBtn.setAttribute('disabled', '');
    const item = { id: Number(this.giftVariantId), quantity: 1, properties: { _gift: 'true' } };
    const done = () => { this._adding = false; if (this._addBtn) this._addBtn.removeAttribute('disabled'); };
    // 2026-09-10 (Jonas: tap did nothing on iPhone): plain /cart/add.js fallback + reload when the theme cart
    // API is missing or rejects, so the gift ALWAYS lands in the cart.
    const fallback = () => fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [item] }) })
      .then((r) => { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(() => { this._neutraliseAddButton(); window.location.reload(); })
      .catch(() => {})
      .finally(done);
    if (!(window.KK && window.KK.cart && typeof window.KK.cart.add === 'function')) { fallback(); return; }
    // Request the cart-drawer section so the drawer re-renders with the gift line.
    const opts = {};
    const drawer = document.querySelector('cart-drawer[data-section-id]');
    if (drawer) opts.sections = [drawer.getAttribute('data-section-id')];
    let settled = false;
    const guard = setTimeout(() => { if (!settled) { settled = true; fallback(); } }, 6000);
    window.KK.cart
      .add(item, opts)
      .then(() => {
        settled = true; clearTimeout(guard); done();
        // Cart PAGE (no drawer re-render) or a drawer that did not re-render: the gift IS in the cart but the bar
        // still shows the add button → reload so the customer sees it (Jonas 2026-09-10: "neprisideda").
        setTimeout(() => {
          if (this.isConnected && this.getAttribute('data-gift-in-cart') !== 'true') window.location.reload();
        }, 1500);
      })
      .catch(() => { if (!settled) { settled = true; clearTimeout(guard); fallback(); } else done(); });
  }

  _onCartUpdated(payload) {
    const cart = (payload && payload.cart) || (window.KK && window.KK.cart && window.KK.cart.state);
    if (!cart) return;

    // Identify the gift line (by _gift property or the gift variant id).
    // Only `_gift` lines are gifts (2026-09-10) - a pillow bought normally is a normal line.
    const isGift = (it) => !!((it.properties || {})._gift);
    const giftLine = (cart.items || []).find(isGift);

    // 2026-09-10 multi-gift: one gift per full threshold (cart total WITHOUT gift lines), capped at data-max-gifts.
    const maxGifts = parseInt(this.getAttribute('data-max-gifts') || '1', 10) || 1;
    const gifts = (cart.items || []).filter(isGift);
    const giftQty = gifts.reduce((n, it) => n + (it.quantity || 0), 0);
    // 2026-09-11: threshold on the PRE-discount value of the non-gift lines (original_line_price) - the discount engine
    // works on original prices too; cart.total_price dropped below 180 on set deals and auto-remove threw the gift out.
    const base = (cart.items || []).filter((it) => !isGift(it)).reduce((n, it) => n + (it.original_line_price || it.line_price || 0), 0);
    const allowed = this.threshold > 0 ? Math.min(maxGifts, Math.floor(base / this.threshold)) : 0;

    // Auto-trim: more gifts in the cart than the total allows (dropped below a tier).
    if (this.autoRemove && giftLine && giftQty > allowed && !this._removing) {
      this._removing = true;
      if (window.KK && window.KK.cart) {
        window.KK.cart.change(giftLine.key, Math.max(0, allowed))
          .catch(() => {})
          .finally(() => { this._removing = false; });
      }
      return;
    }
    if (giftLine && giftQty < allowed) return; // another gift is unlocked - keep the add button live

    // Cart page has no auto re-render: once the gift is in the cart, the stale
    // server-rendered "add" button must not be clickable again — a second click
    // would add a SECOND gift line that the BXGY (limit one) can't zero out.
    if (giftLine) {
      this._neutraliseAddButton();
    }
    // When not auto-removing, the server-rendered hint (data-gift-below-hint) stays
    // visible; the drawer's own section re-render keeps the bar's copy current.
  }

  // Disable + hide the one-tap add button and mark the element as gift-in-cart so
  // the gift cannot be added twice on the cart page (which does not re-render the
  // bar region after a successful add). Idempotent and safe in the drawer too.
  _neutraliseAddButton() {
    this.setAttribute('data-gift-in-cart', 'true');
    const btn = this._addBtn || this.querySelector('[data-gift-add]');
    if (!btn) return;
    btn.setAttribute('disabled', '');
    btn.setAttribute('aria-hidden', 'true');
    btn.hidden = true;
    if (this._onAdd) {
      btn.removeEventListener('click', this._onAdd);
      this._onAdd = null;
    }
  }
}

if (!customElements.get('free-gift-bar')) {
  customElements.define('free-gift-bar', FreeGiftBar);
}
